"use client";

import { useEffect, useRef, useState } from "react";
import { CheckoutDialog } from "@/components/checkout_dialog";
import { PricingCardArtistic } from "@/components/pricing/pricing_card_artistic";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import type { PricingRecord } from "@/lib/pricing-data";
import { findInitialCheckoutPlan, toCheckoutPlan } from "./checkout_launch";
import PriceMatchDialog from "./price_match_dialog";

export default function EngagementPricingTab({
	data,
	country = "usd",
	initialCheckoutPlanId = null,
}: {
	data: PricingRecord[];
	country?: string;
	initialCheckoutPlanId?: number | null;
}) {
	const [selectedPlan, setSelectedPlan] = useState<PricingRecord | null>(null);
	const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
	const openedInitialPlanId = useRef<number | null>(null);

	const handleGetStarted = (plan: PricingRecord) => {
		setSelectedPlan(plan);
		setIsCheckoutOpen(true);
	};
	const isSingleWatchHourPlan = (data?.length ?? 0) === 1;

	useEffect(() => {
		if (
			!initialCheckoutPlanId ||
			openedInitialPlanId.current === initialCheckoutPlanId
		) {
			return;
		}

		const initialPlan = findInitialCheckoutPlan(
			data ?? [],
			initialCheckoutPlanId,
		);
		if (!initialPlan) return;

		setSelectedPlan(initialPlan);
		setIsCheckoutOpen(true);
		openedInitialPlanId.current = initialCheckoutPlanId;
	}, [data, initialCheckoutPlanId]);

	return (
		<>
			<CheckoutDialog
				open={isCheckoutOpen}
				onOpenChange={setIsCheckoutOpen}
				plan={selectedPlan ? toCheckoutPlan(selectedPlan) : null}
				currency={country === "inr" ? "inr" : "usd"}
			/>

			<div className="w-full max-w-3xl mx-auto text-center mb-10">
				<p className="font-inter text-zinc-500 text-base leading-relaxed">
					Long-form engagement campaigns designed to drive deep, sustained
					viewing of your content.
				</p>
			</div>

			<section className="w-full mb-12">
				<div
					className={
						isSingleWatchHourPlan
							? "grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 max-w-3xl mx-auto"
							: `grid grid-cols-1 md:grid-cols-2 ${
									(data?.length ?? 0) % 3 === 0
										? "lg:grid-cols-3"
										: "lg:grid-cols-4"
								} gap-6 max-w-7xl mx-auto`
					}
				>
					{(data ?? []).map((pkg, i) => (
						<div key={pkg.id} className="h-full">
							<PricingCardArtistic
								name={pkg.tier || pkg.name}
								price={country === "inr" ? pkg.price_in_inr : pkg.price_in_usd}
								currency={country}
								features={pkg.features}
								popular={pkg.popular || isSingleWatchHourPlan}
								onGetStarted={() => handleGetStarted(pkg)}
								index={i}
								size={isSingleWatchHourPlan ? "featured" : "default"}
								description="Engagement Campaign"
								categoryIcon="clock"
							/>
						</div>
					))}
				</div>
			</section>

			{/* Requirement Note */}
			<div className="w-full max-w-7xl mx-auto mb-8 mt-4">
				<div className="flex items-center justify-center gap-2 text-sm text-zinc-500 font-inter">
					<span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
					<span>
						{highlightPricingTextFeatures(
							"Requires at least one video with 60+ minutes of content",
						)}
					</span>
				</div>
			</div>

			<PriceMatchDialog />

			<div className="w-full max-w-3xl mx-auto mb-20">
				<div className="text-center mb-12">
					<h2 className="font-syne font-black text-3xl uppercase tracking-tight text-zinc-900">
						Campaign Questions
					</h2>
					<div className="h-1 w-12 bg-zinc-200 mx-auto mt-4 rounded-full" />
				</div>

				<Accordion type="single" collapsible className="w-full space-y-4">
					<AccordionItem
						value="item-4"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Compliance & Safety
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							All campaigns run through official Google Ads — fully compliant,
							fully transparent. No shortcuts, no workarounds.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-2"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Campaign Timeline
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Results typically build over 14-30 days. We pace campaigns for steady, consistent performance throughout your campaign period.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-3"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Full Channel Control
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							We handle the campaign side. You keep full control of your channel
							and any platform applications — with guidance from our team if
							needed.
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		</>
	);
}
